// src/pages/PaidBill.jsx

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { styled } from '@mui/material/styles';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Divider,
  Button,
  Stack,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import epLogo from '../assets/images/ep_logo.png';
import mlLogo from '../assets/images/ml_logo.png';
import useResponsive from '../hooks/useResponsive';
import Iconify from '../components/iconify';
import { useNavigate, useLocation } from 'react-router-dom';

import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import logo from '../assets/images/logo-form.png';
import moment from 'moment';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

// ----------------------------------------------------------------------

const StyledRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  height: '100vh',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    display: 'block',
  },
}));

const StyledSection = styled('div')(({ theme }) => ({
  width: 380,
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  backgroundColor: theme.palette.background.default,
  overflow: 'hidden',
}));

const StyledContent = styled('div')(({ theme }) => ({
  flexGrow: 1,
  height: '100vh',
  overflow: 'auto',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
}));

export default function PaidBill() {
  const navigate = useNavigate();
  const mdUp = useResponsive('up', 'md');
  const { state } = useLocation();
  const item = state?.item;

  // Helper to format dates
  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

  const truncate2 = (num) => Math.floor((num || 0) * 100) / 100;

  // Download PDF using item data
  const handleDownloadPdf = async () => {
    const pageWidthPt = 595.28;
    const marginPt = 40 * 2;
    const contentWidth = pageWidthPt - marginPt;
    const PADDING = 8;
    const BOTTOM_SPACE = 30;

    const currency = item?.currency || 'TRY';
    // net tutar (lineItems toplamı)
    const totalService =
      item?.lineItems?.reduce((sum, li) => sum + truncate2(li.tutar), 0) || 0;
    const totalWithVat = truncate2(item?.price || 0);

    const invoiceData = {
      invoiceNumber: item?.invoiceID || '',
      invoiceNo: item?.invoiceID || '',
      taxNo: item?.taxNo || '',
      invoiceDate: item?.invoiceDate ? formatDate(item.invoiceDate) : '',
      paymentDate: item?.paymentDateTime
        ? formatDate(item.paymentDateTime)
        : '',
      fromName: item?.from.companyTitle || '',
      fromAddress: item?.from.address || '',
      fromPhone: item?.from.phone || '',
      toName: item?.to.companyTitle || '',
      toAddress: item?.to.address || '',
      toPhone: item?.to.phone || '',
      explanationTitle: item?.description || '',
      explanationDetail: item?.description || '',
      quantity: item?.lineItems?.length || 0,
      unitPrice:
        item?.lineItems?.[0]?.tutar?.toLocaleString('tr-TR', {
          style: 'currency',
          currency,
        }) || '',
      subtotal: totalService.toLocaleString('tr-TR', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      total: totalWithVat.toLocaleString('tr-TR', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      conversationID: item?.conversationID || '-',
    };
    const docDefinition = {
      pageSize: 'A4',
      pageMargins: [40, 40, 40, 40],
      content: [
        // Logo & Başlık
        {
          columns: [
            { image: logo, width: 80 },
            {
              width: '*',
              stack: [
                {
                  text: 'AVM Lojistik A.Ş.',
                  fontSize: 12,
                  alignment: 'center',
                },
                {
                  text: 'ÖDEME ALINDISI',
                  fontSize: 18,
                  bold: true,
                  color: '#006366',
                  alignment: 'center',
                  margin: [0, 4, 0, 0],
                },
              ],
            },
          ],
          columnGap: 10,
          margin: [0, 0, 0, 15],
        },

        // Özet Kutusu 1
        {
          stack: [
            {
              canvas: [
                {
                  type: 'rect',
                  x: 0,
                  y: 0,
                  w: contentWidth,
                  h: 98,
                  r: 5,
                  lineColor: '#00B0BD',
                  lineWidth: 1,
                },
              ],
            },
            {
              table: {
                widths: ['auto', 'auto', '*'],
                body: [
                  [
                    { text: 'ÖDEME NUMARASI', bold: true, fontSize: 10 },
                    { text: ':', bold: true, fontSize: 10 },
                    { text: invoiceData?.conversationID, fontSize: 10 },
                  ],
                  [
                    { text: 'FİRMA ÜNVANI', bold: true, fontSize: 10 },
                    { text: ':', bold: true, fontSize: 10 },
                    { text: invoiceData.fromName, fontSize: 10 },
                  ],
                  [
                    { text: 'VERGİ DAİRESİ VE NO', bold: true, fontSize: 10 },
                    { text: ':', bold: true, fontSize: 10 },
                    { text: invoiceData.taxNo, fontSize: 10 },
                  ],
                  [
                    { text: 'ÖDEME TARİHİ', bold: true, fontSize: 10 },
                    { text: ':', bold: true, fontSize: 10 },
                    { text: invoiceData.paymentDate, fontSize: 10 },
                  ],
                  [
                    {
                      text: 'GENEL TOPLAM (KARTTAN ÖDENEN)',
                      bold: true,
                      fontSize: 10,
                    },
                    { text: ':', bold: true, fontSize: 10 },
                    { text: invoiceData.total, fontSize: 10 },
                  ],
                ],
              },
              layout: 'noBorders',
              margin: [PADDING, -(98 - PADDING), PADDING, BOTTOM_SPACE],
            },
          ],
        },

        // Özet Kutusu 2
        {
          stack: [
            {
              canvas: [
                {
                  type: 'rect',
                  x: 0,
                  y: 0,
                  w: contentWidth,
                  h: 80,
                  r: 5,
                  lineColor: '#00B0BD',
                  lineWidth: 1,
                },
              ],
            },
            {
              table: {
                widths: ['*', '*'],
                body: [
                  [
                    { text: 'FATURA NUMARASI :', bold: true, fontSize: 10 },
                    {
                      text: 'TUTARI',
                      bold: true,
                      fontSize: 10,
                      alignment: 'right',
                    },
                  ],
                  [
                    {
                      text: invoiceData.invoiceNo,
                      fontSize: 10,
                    },
                    {
                      text: invoiceData.subtotal,
                      fontSize: 10,
                      alignment: 'right',
                    },
                  ],
                  [
                    {
                      text: '',
                      italics: true,
                      fontSize: 8,
                      colSpan: 2,
                      alignment: 'center',
                    },
                    {},
                  ],
                  [
                    { text: 'TOPLAM TUTAR :', bold: true, fontSize: 10 },
                    {
                      text: invoiceData.total,
                      bold: true,
                      fontSize: 10,
                      alignment: 'right',
                    },
                  ],
                ],
              },
              layout: 'noBorders',
              margin: [PADDING, -(80 - PADDING), PADDING, BOTTOM_SPACE],
            },
          ],
        },

        // Özet Kutusu 3
        {
          stack: [
            {
              canvas: [
                {
                  type: 'rect',
                  x: 0,
                  y: 0,
                  w: contentWidth,
                  h: 60,
                  r: 5,
                  lineColor: '#00B0BD',
                  lineWidth: 1,
                },
              ],
            },
            {
              table: {
                widths: ['*', '*', '*'],
                body: [
                  [
                    { text: 'Tahsilat Şekli :', bold: true, fontSize: 10 },
                    { text: 'İşlem Türü :', bold: true, fontSize: 10 },
                    { text: 'Ödeme Şekli :', bold: true, fontSize: 10 },
                  ],
                  [
                    {
                      text: invoiceData.explanationTitle,
                      fontSize: 10,
                    },
                    {
                      text: invoiceData.explanationDetail,
                      fontSize: 10,
                    },
                    { text: 'KREDİ KARTI', fontSize: 10 },
                  ],
                ],
              },
              layout: 'noBorders',
              margin: [PADDING, -(60 - PADDING), PADDING, 0],
            },
          ],
        },
      ],
    };

    pdfMake
      .createPdf(docDefinition)
      .download(`Odeme_Alindi_${moment().format('DDMMYYYY_HHmm')}.pdf`);
  };

  // hesaplamalar
  const totalService =
    item?.lineItems?.reduce(
      (sum, li) => sum + truncate2((li.tutar || 0) - (li.vergi || 0)),
      0
    ) || 0;

  const totalVat =
    item?.lineItems?.reduce((sum, li) => sum + truncate2(li.vergi || 0), 0) ||
    0;

  const totalWithVat = truncate2(item?.price || 0);

  const displayData = {
    statusText: item?.isPaid ? 'Ödenmiş' : 'Ödenmemiş',
    invoiceNumber: item?.invoiceID,
    invoiceNo: item?.invoiceID,
    taxNo: item?.taxNo,
    invoiceDate: item?.invoiceDate ? formatDate(item.invoiceDate) : '-',
    paymentDate: item?.paymentDateTime ? formatDate(item.paymentDateTime) : '-',
    fromName: item?.from.companyTitle,
    fromAddress: item?.from.address,
    fromPhone: item?.from.phone || '-',
    toName: item?.to.companyTitle,
    toAddress: item?.to.address,
    toPhone: item?.to.phone || '-',

    items:
      item?.lineItems?.map((li, idx) => {
        const tutar = li.tutar || 0;
        const vergi = li.vergi || 0;
        return {
          id: idx + 1,
          description: li.stokKod,
          detail: li.kaynak || '',
          mall: item?.to.mallTitle,
          quantity: li.miktar,

          // Net fiyat (tutar – vergi), truncate + 2 ondalık
          unitPrice: truncate2(tutar - vergi).toLocaleString('tr-TR', {
            style: 'currency',
            currency: item.currency || 'TRY',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),

          vatRate: `${((vergi / (tutar || 1)) * 100).toFixed(0)}%`,

          // Vergi tutarı
          vatAmount: truncate2(vergi).toLocaleString('tr-TR', {
            style: 'currency',
            currency: item.currency || 'TRY',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),

          // Brüt toplam = tutar + vergi
          total: truncate2(tutar).toLocaleString('tr-TR', {
            style: 'currency',
            currency: item.currency || 'TRY',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
        };
      }) || [],

    // Özet kutuları
    totalAmount: truncate2(totalService).toLocaleString('tr-TR', {
      style: 'currency',
      currency: item.currency || 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    vatCalculated: truncate2(totalVat).toLocaleString('tr-TR', {
      style: 'currency',
      currency: item.currency || 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    totalWithVat: truncate2(totalWithVat).toLocaleString('tr-TR', {
      style: 'currency',
      currency: item.currency || 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    payableAmount: truncate2(totalWithVat).toLocaleString('tr-TR', {
      style: 'currency',
      currency: item.currency || 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),

    notes: [
      `${truncate2(totalWithVat).toLocaleString('tr-TR', {
        style: 'currency',
        currency: item.currency || 'TRY',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} dir.`,
      'Vade : 30 Gün',
      'Vadesinde ödenmeyen faturalar için hizmet durdurma ve aylık %3,5 gecikme faizi uygulanır.',
    ],
  };

  return (
    <>
      <Helmet>
        <title>Fatura Ödeme | Mall Logistics Panel</title>
      </Helmet>

      <StyledRoot>
        {mdUp && (
          <StyledSection sx={{ justifyContent: 'center' }}>
            <Box
              sx={{
                backgroundColor: '#103996',
                display: 'flex',
                justifyContent: 'center',
                p: 2,
              }}
            >
              <img src="/assets/logo.png" alt="Logo" />
            </Box>
            <Typography variant="h3" sx={{ px: 5, mt: 5, mb: 5 }}>
              E-Ödeme Servisi
            </Typography>
            <Box sx={{ px: 5 }}>
              <img
                src="/assets/illustrations/login-ill.png"
                alt="login-ill"
                style={{ width: '100%' }}
              />
            </Box>
          </StyledSection>
        )}

        <StyledContent>
          <Box sx={{ width: '100%', maxWidth: 800, margin: 'auto' }}>
            {!item ? (
              <Typography sx={{ textAlign: 'center' }}>
                Fatura verisi bulunamadı.
              </Typography>
            ) : (
              <Card>
                <CardContent>
                  {/* PDF İndir */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-end"
                    spacing={1}
                    sx={{ mb: 1 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        cursor: 'pointer',
                        textDecoration: 'none !important',
                      }}
                      onClick={handleDownloadPdf}
                    >
                      Makbuzu indir
                    </Typography>
                    <Iconify
                      icon="material-symbols:download-rounded"
                      style={{ cursor: 'pointer' }}
                      onClick={handleDownloadPdf}
                    />
                  </Stack>

                  {/* Başlık & Durum */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 2 }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Iconify
                        icon="lets-icons:check-fill"
                        color={item.isPaid ? 'green' : 'gray'}
                        style={{ width: 22, height: 22 }}
                      />
                      <Typography variant="h6">
                        Fatura Ödeme{' '}
                        <span
                          style={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: '#C3C3C3',
                          }}
                        >
                          ({displayData.statusText})
                        </span>
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ color: '#979797' }}>
                      {displayData.invoiceNumber}
                    </Typography>
                  </Stack>

                  <Divider sx={{ mb: 2 }} />

                  {/* Mevcut Fatura Detayları */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 2,
                    }}
                  >
                    <img src={mlLogo} alt="ML Logo" style={{ height: 120 }} />
                    <img src={epLogo} alt="EP Logo" style={{ height: 100 }} />
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary' }}
                        >
                          Fatura No
                        </Typography>
                        <Typography variant="subtitle2">
                          {displayData.invoiceNo}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary' }}
                        >
                          Vergi No
                        </Typography>
                        <Typography variant="subtitle2">
                          {displayData.taxNo}
                        </Typography>
                      </Stack>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary', fontWeight: 'bold' }}
                        >
                          Kimden
                        </Typography>
                        <Typography variant="subtitle2">
                          {displayData.fromName}
                        </Typography>
                        <Typography variant="body2">
                          {displayData.fromAddress}
                        </Typography>
                        <Typography variant="body2">
                          {displayData.fromPhone}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={2}>
                        <Box>
                          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary' }}
                            >
                              Fatura Tarihi
                            </Typography>
                            <Typography variant="subtitle2">
                              {displayData.invoiceDate}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary' }}
                            >
                              Ödeme Tarihi
                            </Typography>
                            <Typography variant="subtitle2">
                              {displayData.paymentDate}
                            </Typography>
                          </Stack>
                        </Box>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary', fontWeight: 'bold' }}
                          >
                            Kime
                          </Typography>
                          <Typography variant="subtitle2">
                            SAYIN {displayData.toName}
                          </Typography>
                          <Typography variant="body2">
                            {displayData.toAddress}
                          </Typography>
                          <Typography variant="body2">
                            {displayData.toPhone}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>

                  {/* Tablo */}
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Sn</TableCell>
                          <TableCell>Mal / Hizmet</TableCell>
                          <TableCell>Açıklama</TableCell>
                          <TableCell>AVM Adı</TableCell>
                          <TableCell align="right">Miktar</TableCell>
                          <TableCell align="right">Birim Fiyat</TableCell>
                          <TableCell align="right">KDV %</TableCell>
                          <TableCell align="right">KDV Tutarı</TableCell>
                          <TableCell align="right">Toplam</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displayData.items.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>{row.id}</TableCell>
                            <TableCell>{row.description}</TableCell>
                            <TableCell>{row.detail}</TableCell>
                            <TableCell>{row.mall}</TableCell>
                            <TableCell align="right">{row.quantity}</TableCell>
                            <TableCell align="right">{row.unitPrice}</TableCell>
                            <TableCell align="right">20%</TableCell>
                            <TableCell align="right">{row.vatAmount}</TableCell>
                            <TableCell align="right">{row.total}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    {/* Soldaki özet kutusu */}
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          border: '1px solid rgba(0,0,0,0.3)',
                          borderRadius: '10px',
                          p: 1,
                          maxWidth: 360,
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Grid container spacing={1}>
                          <Grid item xs={12} md={6}>
                            <Stack spacing={0.5}>
                              <Typography variant="caption" fontWeight="bold">
                                Versiyon : {item?.version || '-'}
                              </Typography>
                              <Typography variant="caption" fontWeight="bold">
                                Senaryo : {item?.scenario || '-'}
                              </Typography>
                              <Typography variant="caption" fontWeight="bold">
                                Fatura Tipi : {item?.invoiceType || '-'}
                              </Typography>
                            </Stack>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Stack spacing={0.5}>
                              <Typography variant="caption" fontWeight="bold">
                                Fatura No : {displayData.invoiceNo}
                              </Typography>
                              <Typography variant="caption" fontWeight="bold">
                                Fatura Tarihi : {displayData.invoiceDate}
                              </Typography>
                              <Typography variant="caption" fontWeight="bold">
                                Fatura Vadesi :{' '}
                                {item?.dueDate ? formatDate(item.dueDate) : '-'}
                              </Typography>
                              <Typography variant="caption" fontWeight="bold">
                                Conversation ID : {item?.conversationID || '-'}
                              </Typography>
                            </Stack>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>

                    {/* Sağdaki alt toplamlar kutusu */}
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          border: '1px solid rgba(0,0,0,0.2)',
                          p: 2,
                          borderRadius: '5px',
                        }}
                      >
                        <Stack spacing={1}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2">
                              Mal Hizmet Toplam Tutarı
                            </Typography>
                            <Typography variant="body2">
                              {displayData.totalAmount}
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2">
                              Hesaplanan KDV
                            </Typography>
                            <Typography variant="body2">
                              {displayData.vatCalculated}
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2">
                              Vergiler Dahil Toplam Tutar
                            </Typography>
                            <Typography variant="body2">
                              {displayData.totalWithVat}
                            </Typography>
                          </Stack>
                          <Divider />
                          <Stack direction="row" justifyContent="space-between">
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 'bold' }}
                            >
                              Ödenecek Tutar
                            </Typography>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 'bold' }}
                            >
                              {displayData.payableAmount}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Açıklamalar */}
                  <Box
                    sx={{
                      mt: 2,
                      pt: 2,
                      borderTop: '1px solid rgba(0,0,0,0.2)',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      AÇIKLAMALAR
                    </Typography>
                    {displayData.notes.map((note, i) => (
                      <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                        {note}
                      </Typography>
                    ))}
                  </Box>
                </CardContent>

                <CardActions sx={{ flexDirection: 'column', p: 2 }}>
                  <Button disabled variant="contained" fullWidth sx={{ mb: 1 }}>
                    {item.isPaid ? 'Faturanız Ödenmiştir' : 'Ödeme Bekleniyor'}
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    fullWidth
                    onClick={() => navigate('/pay-bill')}
                    style={{ marginLeft: '0px' }}
                  >
                    Yeni Bir Fatura Öde
                  </Button>
                </CardActions>
              </Card>
            )}
          </Box>
        </StyledContent>
      </StyledRoot>
    </>
  );
}
